import Image from "next/image";
import React from "react";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { Platform } from "@/models/GameResell";
import { PDP_TEXT_LINK } from "@/components/tactsuit/pdp-text-link";
import { useProductGameKeys } from "@/components/tactsuit/components/buy-tactsuit/ProductGameKeys";
import {
  EXOSHOCK_COMPONENT_SKU,
  GAMEKEY_DETAIL_ANCHOR,
} from "@/components/tactsuit/components/buy-tactsuit/bundle-detail-rail";
import { bundleOnlyGameKeyImage } from "@/libs/games/bundle-only-game-key";
import MetaBlueIcon from "@/v2-components/core/icon/meta-icon-blue";
import SteamBlueIcon from "@/v2-components/core/icon/steam-icon-blue";
import { useProducts } from "@/services/product/useProductService";
import { ProductUtil } from "@/utils/ProductUtil";

/**
 * EXOSHOCK 번들 전용 PDP 본문 섹션 — 피그마 `8X5fPtMR3n9lVij5MJKaUh` 섹션 `31584:38289`
 * (데스크톱 `31584:38290` · 모바일 `31584:38771`, 2026-10-28 대조).
 *
 * 순서(시안 본문 y): EventBanner → Purchase Benefits → Platform → Bundle Contents → Gamekey(A)
 * → Badge → Mesh Lining → Native Haptics → Companion Software … — 조립은 `ProductFeatureSections`.
 *
 * ⚠️ 디자인 진행 중이다 — 시안 재확인 때마다 이미지가 채워진다(2026-10-28 4회차: 배지 · 메시 · 네이티브 사진, 구성품 메시 칸).
 */
const IMG = "/images/tactsuit/exoshock-bundle";

/* ------------------------------ Event Banner ------------------------------ */

/**
 * 히어로 — Full-bleed(데스크톱 1920×640 `31657:6861` · 모바일 360×430 `31657:6877`, 2026-10-28 재확인 — 528 에서 바뀜).
 * 공용 히어로(`ProductHeroAndBenefits` 의 1440 카드형)를 대신한다.
 * 모바일은 이미지(360×240) 아래에 카피 블록이 붙고(페이드 없음), 데스크톱은 1920×640 한 장 위에 우측 카피다
 * (2026-10-28 6회차: 데스크톱 이미지가 「좌측 이미지 + 페이드」에서 한 장짜리로, 아이브로가 텍스트에서 로고 둘로 바뀌었다).
 */
export function ExoshockEventBanner() {
  const { t } = useTrans(["tactsuit"]);
  return (
    <section className="relative w-full overflow-hidden bg-[#03080c] text-white laptop:h-[640px]">
      {/* 모바일 이미지 360×240 — 카피와 겹치지 않는다(시안 Image 다음 Copy 가 흐름으로 이어진다) */}
      <div className="relative aspect-[360/240] w-full laptop:hidden">
        <Image
          src={`${IMG}/event-banner-3-mobile.jpg`}
          alt=""
          fill
          sizes="100vw"
          className="object-cover"
        />
      </div>
      {/* 데스크톱 이미지 — 캔버스 전체(1920×640) */}
      <div className="absolute inset-0 hidden laptop:block">
        <Image
          src={`${IMG}/event-banner-3-desktop.jpg`}
          alt=""
          fill
          sizes="100vw"
          className="object-cover"
        />
      </div>
      {/* 카피 — 모바일 가운데 정렬 gap 8 · px 16 py 24 · 데스크톱 left 1114/1920 · 좌측 정렬 gap 16 */}
      <div className="relative flex flex-col items-center gap-2 px-4 py-6 text-center laptop:absolute laptop:left-[58%] laptop:top-1/2 laptop:w-[560px] laptop:max-w-[37%] laptop:-translate-y-1/2 laptop:items-start laptop:gap-4 laptop:p-0 laptop:text-left">
        {/* 아이브로 — EXOSHOCK 워드마크 × bHaptics 로고(모바일 162.6×20 · × 7 · 80×24 · gap 10 /
            데스크톱 260×32 · × 10 · 133×40 · gap 20 — 시안 `Eyebrow` 31761:39209 · 31761:39219).
            「×」는 글자가 아니라 흰 선 벡터(`Times` 31766:56907 · 두께 2, 모바일은 같은 비율로 줄어 1.4 — 2026-10-29 재확인) */}
        <div className="flex items-center gap-2.5 laptop:gap-5">
          <Image
            src={`${IMG}/exoshock-wordmark.svg`}
            alt="EXOSHOCK"
            width={260}
            height={32}
            unoptimized
            className="h-5 w-[162.6px] laptop:h-8 laptop:w-[260px]"
          />
          <svg
            viewBox="0 0 10 10"
            overflow="visible"
            aria-label="×"
            className="size-[7px] shrink-0 laptop:size-2.5"
          >
            <path d="M0 0L10 10M10 0L0 10" stroke="white" strokeWidth={2} />
          </svg>
          <span className="relative h-6 w-20 laptop:h-10 laptop:w-[133px]">
            <Image
              src={`${IMG}/bhaptics-logo-white.svg`}
              alt="bHaptics"
              fill
              unoptimized
              className="!inset-x-[2.5%] !inset-y-[12.5%] !h-3/4 !w-[95%]"
            />
          </span>
        </div>
        <p className="text-[26px] leading-[34px] laptop:text-[48px] laptop:leading-[56px]">
          {t("FEEL THE FIGHT, HEAD TO TOE.")}
        </p>
        <p className="text-base leading-5 laptop:text-lg laptop:leading-6">
          {t(
            "Native haptic support for EXOSHOCK. Go deeper into the battlefield with TactSuit Air.",
          )}
        </p>
      </div>
    </section>
  );
}

/* ---------------------------- Platform 섹션 노트 ---------------------------- */

/** 「Immersion Across Every Platform」 카드 아래 한 줄(데스크톱 `31600:38895` · 모바일 `31602:38882`) */
export function ExoshockPlatformNote() {
  const { t } = useTrans(["tactsuit"]);
  return (
    <p className="mx-4 mt-6 text-center text-base tracking-[0.25px] text-coolgray-90 laptop:mt-8 laptop:text-lg laptop:tracking-[0.5px]">
      {t(
        "Supported games and connection methods vary by platform. The EXOSHOCK Gamekey supports Steam and Meta Quest.",
      )}
    </p>
  );
}

/* ----------------------------- Bundle Contents ----------------------------- */

type ContentItem = { name: string; key: string; media: React.ReactNode };

/**
 * 번들 구성품 4칸(데스크톱 `31585:39218` 192 칸 · 모바일 `31595:39035` 140 칸 2열).
 * 이미지는 **어드민**이다(2026-10-28 결정) — 배지 · 메시 라이닝 = 구성품 SKU 썸네일, 게임 키 = 게임 리셀 이미지.
 * 비어 있으면 그 칸은 빈 자리다(이름만).
 *
 * ⚠️ Air 칸만 예외 — 가슴 와펜을 EXOSHOCK 배지로 바꾼 콜랩 합성 이미지(시안 `exoshock-bundle-air-01` 31717:6852,
 * 2026-10-29 교체)라 어드민에 실을 자리가 없다. Air Onyx SKU 썸네일을 바꾸면 Air PDP · 장바구니 · 주문까지 바뀐다.
 * CDN 주소를 고정한다(사용자 결정 2026-10-29).
 */
const EXOSHOCK_AIR_TILE_IMAGE =
  "https://cdn.bhaptics.com/images/products/exoshock-bundle-air-01.png";

export function ExoshockBundleContents() {
  const { t } = useTrans(["tactsuit"]);
  const { data: products = [] } = useProducts();
  const games = useProductGameKeys();
  const sku = (s: string) =>
    ProductUtil.getSkuThumbnail(products, s) || undefined;
  const gamekey = bundleOnlyGameKeyImage(games[0]?.gameResell);
  const fill = (src: string | undefined, sizes: string, className: string) =>
    src ? (
      <Image src={src} alt="" fill sizes={sizes} className={className} />
    ) : null;

  const items: ContentItem[] = [
    {
      key: "air",
      // 제품명·색상명은 번역하지 않는다
      name: "TactSuit Air\n(Onyx)",
      media: fill(
        EXOSHOCK_AIR_TILE_IMAGE,
        "(min-width: 1024px) 192px, 140px",
        "object-contain",
      ),
    },
    {
      key: "gamekey",
      name: `EXOSHOCK\n${t("Gamekey")}`,
      // 칸 안에서 작게 — 데스크톱 192 칸에 120 · 모바일 140 칸에 96, 모서리 12(시안 `Wrapper` 31712:124516 · 31712:124517)
      media: (
        <div className="absolute inset-[22px] laptop:inset-9">
          {fill(
            gamekey,
            "(min-width: 1024px) 120px, 96px",
            "rounded-xl object-cover",
          )}
        </div>
      ),
    },
    {
      key: "badge",
      name: `EXOSHOCK\n${t("Exclusive Badge")}`,
      // 어드민 배지 이미지가 이미 10% 여백을 품은 1000² 다(시안 inset 10% 와 같은 모양)
      media: fill(
        sku(EXOSHOCK_COMPONENT_SKU.badge),
        "(min-width: 1024px) 192px, 140px",
        "object-contain",
      ),
    },
    {
      key: "mesh",
      name: `EXOSHOCK\n${t("Custom Mesh Lining")}`,
      media: fill(
        sku(EXOSHOCK_COMPONENT_SKU.meshLining),
        "(min-width: 1024px) 192px, 140px",
        "object-contain",
      ),
    },
  ];

  return (
    <section className="mx-auto flex w-full max-w-screen-mdl flex-col items-center gap-6 rounded-2xl bg-coolgray-10 px-4 py-7 laptop:p-10">
      <h2 className="text-center text-3xl text-coolgray-90 laptop:text-5xl">
        {t("Bundle Contents")}
      </h2>
      <ul className="flex flex-wrap justify-center gap-x-4 gap-y-6 laptop:flex-nowrap laptop:gap-5">
        {items.map((item) => (
          <li
            key={item.key}
            className="flex w-[140px] flex-col items-center gap-5 laptop:w-48 laptop:gap-6"
          >
            <div className="relative size-[140px] laptop:size-48">
              {item.media}
            </div>
            <p className="whitespace-pre-line text-center text-base font-500 tracking-[0.1px] text-coolgray-90">
              {item.name}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ------------------------------ Gamekey 섹션 ------------------------------ */

/** 「About EXOSHOCK」 등 외부 링크 — 시안 `Icons/new-24px`(open_in_new) 18 · gap 8 · pr 16 */
function ExternalTextLink({
  href,
  label,
  className,
}: {
  href: string;
  label: string;
  className?: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(PDP_TEXT_LINK, "gap-2 pr-4", className)}
    >
      {label}
      <Image
        src={`${IMG}/icon-new.svg`}
        alt=""
        width={18}
        height={18}
        unoptimized
      />
    </a>
  );
}

/**
 * 게임 소개 링크 — 게임 키 데이터의 Steam 스토어 링크(`platformList[].gameLink`).
 * 링크가 비어 있으면 버튼을 그리지 않는다(빈 href 로 현재 페이지가 새 탭에 열리는 것을 막는다).
 */
function useExoshockGameLink(): string | undefined {
  const games = useProductGameKeys();
  const platforms = games[0]?.gameResell.platformList ?? [];
  return (
    platforms.find((p) => p.platform === Platform.Steam)?.gameLink ||
    platforms[0]?.gameLink ||
    undefined
  );
}

const INFO_LABEL =
  "text-sm font-600 uppercase tracking-[0.72px] text-coolgray-60";
const INFO_VALUE = "text-[15px] font-600 text-coolgray-90 laptop:text-lg";

/**
 * 게임 키 소개 — Variant A(Key Art Banner, 시안 「현재 메인 적용 = A」 `31657:7259`).
 * 데스크톱 `31657:7031`(py 96 · 배너 1196×504 · 2열 586) · 모바일 `31657:7062`(px 16 · py 48 · 배너 328×246).
 * 레일 「View Gamekey details ↓」의 목적지(`#gamekey`).
 */
export function ExoshockGamekeySection() {
  const { t } = useTrans(["tactsuit"]);
  const gameLink = useExoshockGameLink();

  return (
    <section
      id={GAMEKEY_DETAIL_ANCHOR}
      className="mx-auto flex w-full max-w-screen-mdl flex-col gap-8 px-4 py-12 laptop:gap-12 laptop:px-0 laptop:py-24"
    >
      <div className="relative h-[246px] w-full overflow-hidden rounded-lg laptop:h-[504px]">
        <Image
          src={`${IMG}/keyart.jpg`}
          alt=""
          fill
          sizes="(min-width: 1240px) 1196px, 100vw"
          className="object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/0 from-35% to-black/85" />
        <div className="absolute bottom-5 left-5 flex flex-col gap-2.5 laptop:bottom-12 laptop:left-12 laptop:gap-4">
          <p className="text-sm font-600 uppercase tracking-[0.96px] text-white laptop:text-[13px] laptop:tracking-[1.04px]">
            {t("Included in the Bundle")}
          </p>
          <Image
            src={`${IMG}/exoshock-logo.svg`}
            alt="EXOSHOCK"
            width={520}
            height={64}
            unoptimized
            className="h-[30px] w-[244px] laptop:h-16 laptop:w-[520px]"
          />
        </div>
      </div>

      <div className="flex flex-col gap-8 laptop:flex-row laptop:gap-6">
        {/* Story — 데스크톱 586 · px 100 */}
        <div className="flex flex-col laptop:w-1/2 laptop:px-[100px]">
          <h2 className="text-[28px] font-700 leading-[34px] text-coolgray-90 laptop:text-[40px] laptop:leading-[48px]">
            {t("EXOSHOCK Gamekey")}
          </h2>
          <p className="mt-3 text-[17px] font-500 text-coolgray-90/70 laptop:text-[20px]">
            {t("Your suit, and your game too.")}
          </p>
          <p className="mt-6 text-[15px] leading-6 text-coolgray-90 laptop:mt-8 laptop:text-lg laptop:leading-[26px]">
            {t(
              "A 1–4 player co-op PvE shooter set in deep space. Breach starships with your squad and take on ever-changing missions and fierce combat.",
            )}
          </p>
          <p className="mt-4 text-[15px] leading-6 text-coolgray-90 laptop:text-lg laptop:leading-[26px]">
            {t(
              "This collab bundle includes an EXOSHOCK Gamekey. Feel every battle in the game with your whole body through TactSuit Air's native haptics.",
            )}
          </p>
        </div>
        {/* Details — 정보 2행 + 안내 + 링크 */}
        <div className="flex flex-col laptop:w-1/2 laptop:px-[100px]">
          <dl className="flex flex-col">
            <div className="flex flex-col gap-1 border-b border-coolgray-20 py-4">
              <dt className={INFO_LABEL}>{t("Supported Platforms")}</dt>
              {/* 아이콘 + 이름(시안 `Value` 31800:39528 — 데스크톱 아이콘 20 · 간격 6 · 항목 사이 16 / 모바일 18 · 4 · 12,
                  2026-10-29). 아이콘은 Select Game Key 와 같은 리포 아이콘 — 그라디언트 색값까지 시안과 같다.
                  플랫폼명은 번역하지 않는다 */}
              <dd
                className={cn(
                  INFO_VALUE,
                  "flex flex-wrap items-center gap-x-3 gap-y-1 laptop:gap-x-4",
                )}
              >
                <span className="flex items-center gap-1 laptop:gap-1.5">
                  <SteamBlueIcon className="size-[18px] shrink-0 laptop:size-5" />
                  Steam
                </span>
                <span className="flex items-center gap-1 laptop:gap-1.5">
                  <MetaBlueIcon className="size-[18px] shrink-0 laptop:size-5" />
                  Meta Quest
                </span>
              </dd>
            </div>
            <div className="flex flex-col gap-1 border-b border-coolgray-20 py-4">
              {/* 라벨 전용 키 — 구성품 이름의 「Gamekey」와 한국어가 다르다(기획안: 「게임 키 발급」) */}
              <dt className={INFO_LABEL}>{t("exoshockGamekeyLabel")}</dt>
              <dd className={INFO_VALUE}>{t("Issued upon purchase")}</dd>
            </div>
          </dl>
          <p className="mt-4 text-sm leading-[18px] text-coolgray-90/55 laptop:text-[13px] laptop:leading-5">
            {t(
              "Register the issued key on its platform to play. Regions where the key can be used will be announced together with the sales details.",
            )}
          </p>
          {gameLink && (
            <ExternalTextLink
              href={gameLink}
              label={t("About EXOSHOCK")}
              className="mt-6"
            />
          )}
        </div>
      </div>
    </section>
  );
}

/* ------------------------- Badge · Mesh · Native ------------------------- */

/**
 * 좌우 분할 소개 — 데스크톱 586 + 24 + 586(미디어 r16 정사각) · 모바일 미디어 328 위 + 카피 아래(gap 24).
 * 데스크톱 카피 열은 px 100 · 세로 가운데. 모바일은 **항상 미디어가 먼저**다(시안 3개 모두).
 */
function ExoshockSplitSection({
  id,
  media,
  mediaFirstOnDesktop,
  title,
  children,
  copyClassName,
}: React.PropsWithChildren<{
  id?: string;
  media: React.ReactNode;
  /** 데스크톱에서 미디어가 왼쪽이면 true(Mesh Lining) */
  mediaFirstOnDesktop?: boolean;
  title: string;
  /** 모바일 카피 정렬 등 섹션별 차이 */
  copyClassName?: string;
}>) {
  return (
    <section
      id={id}
      className="mx-auto grid w-full max-w-screen-mdl grid-cols-1 gap-6 laptop:grid-cols-2 laptop:items-center"
    >
      <div
        className={cn(
          "relative aspect-square w-full overflow-hidden rounded-2xl",
          !mediaFirstOnDesktop && "laptop:order-1",
        )}
      >
        {media}
      </div>
      <div
        className={cn(
          "flex flex-col gap-3 text-coolgray-90 laptop:gap-5 laptop:px-[100px]",
          copyClassName,
        )}
      >
        <h2 className="whitespace-pre-line text-3xl laptop:text-5xl">
          {title}
        </h2>
        {children}
      </div>
    </section>
  );
}

const BODY = "text-base tracking-[0.25px]";

/**
 * 배지 소개(데스크톱 `31616:38896` · 모바일 `31584:39113`) — 레일 「View badge details ↓」 목적지.
 * 미디어 = 연출 사진(꽉 채움 · r16). 3회차엔 모바일만 배지 렌더였는데 4회차(2026-10-28)에 모바일도 같은 사진이 됐다.
 */
export function ExoshockBadgeSection({ id }: { id: string }) {
  const { t } = useTrans(["tactsuit"]);
  return (
    <ExoshockSplitSection
      id={id}
      title={t("EXOSHOCK Exclusive Badge")}
      media={
        <Image
          src={`${IMG}/badge-photo-2.jpg`}
          alt=""
          fill
          sizes="(min-width: 1240px) 586px, (min-width: 1024px) 50vw, 100vw"
          className="object-cover"
        />
      }
    >
      <div className={BODY}>
        <p>{t("White EXS logo, red ring.")}</p>
        <p>
          {t(
            "Add the collab-exclusive badge to the chest of your Air and complete the EXOSHOCK identity.",
          )}
        </p>
      </div>
      <p className={BODY}>
        {t("Velcro-backed for easy attaching and detaching.")}
      </p>
    </ExoshockSplitSection>
  );
}

/**
 * 메시 라이닝 소개(데스크톱 `31584:38596` · 모바일 `31584:39129`).
 * 사진은 데스크톱 · 모바일 같은 컷이다(2026-10-28 4회차 — 3회차 컷에서 교체됐다).
 */
export function ExoshockMeshLiningSection({ id }: { id: string }) {
  const { t } = useTrans(["tactsuit"]);
  return (
    <ExoshockSplitSection
      id={id}
      mediaFirstOnDesktop
      // 메시 라이닝만 데스크톱 제목↔본문이 12 다(시안 Stack gap 12 — 배지·네이티브는 20)
      copyClassName="laptop:gap-3"
      title={t("EXOSHOCK Exclusive\nMesh Lining")}
      media={
        <Image
          src={`${IMG}/mesh-photo-3.jpg`}
          alt=""
          fill
          sizes="(min-width: 1240px) 586px, (min-width: 1024px) 50vw, 100vw"
          className="object-cover"
        />
      }
    >
      <p className={BODY}>
        {t(
          "Complete a suit that's all your own with a custom mesh lining featuring the EXOSHOCK logo and DIV.09 graphics.",
        )}
      </p>
    </ExoshockSplitSection>
  );
}

/** 네이티브 햅틱 소개(데스크톱 `31584:38603` · 모바일 `31584:39134`) — 사진은 두 폭 같은 컷(2026-10-28 4회차) */
export function ExoshockNativeHapticsSection() {
  const { t } = useTrans(["tactsuit"]);
  const gameLink = useExoshockGameLink();
  return (
    <ExoshockSplitSection
      title={t("Native Haptics,\nConnected Directly to the Game")}
      media={
        <Image
          src={`${IMG}/native-photo-3.jpg`}
          alt=""
          fill
          sizes="(min-width: 1240px) 586px, (min-width: 1024px) 50vw, 100vw"
          className="object-cover"
        />
      }
    >
      {/* 본문 — 모바일 14/18 ls .25 · 데스크톱 16/22 ls .5 */}
      <div className="text-base tracking-[0.25px] laptop:text-lg laptop:tracking-[0.5px]">
        <p>{t("16 haptic points on TactSuit Air.")}</p>
        <p>
          {t(
            "Experience a new level of immersion with haptics synced to EXOSHOCK.",
          )}
        </p>
      </div>
      {gameLink && (
        // 모바일은 오른쪽 정렬(시안 Copy `items-end`), 데스크톱은 왼쪽
        <ExternalTextLink
          href={gameLink}
          label={t("Learn more about the game")}
          className="self-end laptop:self-start"
        />
      )}
    </ExoshockSplitSection>
  );
}

/* --------------------------- Product Information --------------------------- */

/**
 * 레일 「Product Information」 — 번들 전용 문구(시안 `ProductInformation` 31584:38474 · 모바일 31584:38936).
 * 첫 문장(16/22 w500) → 구성품 목록(14/18 불릿) → 게임 키 안내(16/22 w500), gap 12.
 *
 * ⚠️ 어드민 Product Sections 를 쓰지 않는다 — 공용 규칙은 구성품(Air) 섹션이 먼저라 번들 섹션이 안 보이고,
 * 「제목 + 항목」 구조로는 시안의 첫 문장 · 목록 · 끝 문장 모양이 안 나온다(사용자 결정 2026-10-29).
 * 시안 끝의 「Price and sales schedule will be announced later.」 는 뺐다 — 가격 미정 상태를 만들지 않는다(FAQ 와 같은 결정).
 */
export function ExoshockProductInformation() {
  const { t } = useTrans(["tactsuit"]);
  const lead = "text-lg font-500 tracking-[0.15px]";
  return (
    <div className="flex flex-col gap-3 text-coolgray-90">
      <p className={lead}>
        {t(
          "A limited-edition collab bundle that brings TactSuit Air (Onyx) and EXOSHOCK together.",
        )}
      </p>
      <ul className="list-outside list-disc pl-5 text-base tracking-[0.25px]">
        {/* 제품명 · 색상명은 번역하지 않는다 */}
        <li>TactSuit Air (Onyx)</li>
        <li>{`EXOSHOCK ${t("Gamekey")}`}</li>
        <li>{t("EXOSHOCK Exclusive Badge")}</li>
        <li>{t("EXOSHOCK Custom Mesh Lining")}</li>
      </ul>
      <p className={lead}>
        {t(
          "The EXOSHOCK Gamekey supports Steam and Meta Quest and is issued when you purchase the bundle.",
        )}
      </p>
    </div>
  );
}
